import { produce } from 'immer';
import { createSelector } from 'reselect';
import bbox from '@turf/bbox';
import union from '@turf/union';
import area from '@turf/area';
import { feature, featureCollection } from '@turf/helpers';
import { ScopedType, ScopedAsyncType } from './scoped-type';
import { asyncAction } from './async-action';
import * as api from '../../lib/api';
import * as logger from '../../lib/logger';
import { actions as mapActions } from './map-dux';

const SLICE = 'fbm';
const Type = ScopedType('FBM');
const AsyncType = ScopedAsyncType('FBM');

const ADD_BOUNDARY_CHANGE = Type('ADD_BOUNDARY_CHANGE');
const CLEAR_FAILURE = Type('CLEAR_FAILURE');
const CLEAR_RESULTS = Type('CLEAR_RESULTS');
const COMBINE_FIELDS = AsyncType('COMBINE_FIELDS');
const DELETE_FIELDS = AsyncType('DELETE_FIELD');
const EXPLODE_FIELD = AsyncType('EXPLODE_FIELD');
const FIELDS_CERTIFIED = Type('FIELDS_CERTIFIED');
const FIELDS_UNCERTIFIED = Type('FIELDS_UNCERTIFIED');
const LOAD_FIELDS = AsyncType('LOAD_FIELDS');
const REASSIGN_FIELDS = AsyncType('REASSIGN_FIELDS');
const REDO = Type('REDO');
const RESET_BOUNDARY_CHANGES = Type('RESET_BOUNDARY_CHANGES');
const SAVE_ATTRIBUTES = AsyncType('SAVE_ATTRIBUTES');
const SAVE_FIELD = AsyncType('SAVE_FIELD');
const SET_MAP = Type('SET_MAP');
const SET_SELECTED_FIELDS = Type('SET_SELECTED_FIELDS');
const SPLIT_FIELD = AsyncType('SPLIT_FIELD');
const TOGGLE_FIELD_FOCUS = Type('TOGGLE_FIELD_FOCUS');
const TOGGLE_FIELD_SELECTION = Type('TOGGLE_FIELD_SELECTION');
const UNDO = Type('UNDO');
const VALIDATE_FIELDS = AsyncType('VALIDATE_FIELDS');
const WORKING = Type('WORKING');

const EMPTY = [];

export const selectors = {
  error: (state) => Boolean(state[SLICE].error),
  isWorking: (state) => Boolean(state[SLICE].working),
  selectedFields: (state) => state[SLICE].selected || EMPTY,
  focusedField: (state) => state[SLICE].focused,
  canUndo: (state) =>
    state[SLICE].boundaryChanges?.length >
    state[SLICE].boundaryChangesIndex + 1,
  canRedo: (state) => state[SLICE].boundaryChangesIndex > 0,
  currentBoundaryIndex: (state) => state[SLICE].boundaryChangesIndex,
  validationResults: (state) => state[SLICE].validationResults,
  certificationResults: (state) => state[SLICE].certificationResults,
  uncertificationResults: (state) => state[SLICE].uncertificationResults,

  currentBoundary: createSelector(
    (state) => state[SLICE].boundaryChanges,
    (state) => state[SLICE].boundaryChangesIndex,
    (stack, index) => stack?.[index],
  ),

  uncertifiedFields: createSelector(
    (state) => state[SLICE].fields,
    (fields) => fields?.filter((f) => !f.isCertified),
  ),

  certifiedFields: createSelector(
    (state) => state[SLICE].fields,
    (fields) => fields?.filter((f) => f.isCertified),
  ),

  bounds: createSelector(
    (state) => state[SLICE].fields,
    (fields) => buildBoundingBox(fields),
  ),

  selectedBounds: createSelector(
    (state) => state[SLICE].fields,
    (state) => state[SLICE].selected,
    (fields, selected) =>
      fields &&
      selected?.length &&
      buildBoundingBox(fields.filter((f) => selected.includes(f.aaManagedId))),
  ),

  focusedBounds: createSelector(
    (state) => state[SLICE].fields,
    (state) => state[SLICE].focused,
    (fields, focused) =>
      fields &&
      focused &&
      buildBoundingBox(fields.filter((f) => focused === f.aaManagedId)),
  ),
};

/* prettier-ignore */
export const actions = {
  fieldsCertified,
  fieldsUncertified,
  clearFailure: () => ({ type: CLEAR_FAILURE, payload: null }),
  clearResults: () => ({ type: CLEAR_RESULTS }),
  combineFields,
  deleteFields,
  explodeField,
  loadFields,
  pushBoundaryChange,
  redo: () => ({ type: REDO }),
  resetBoundaryChanges: () => ({ type: RESET_BOUNDARY_CHANGES, payload: [] }),
  saveAttributes,
  reassignFields,
  saveField,
  setMap: (map) => ({ type: SET_MAP, payload: map }),
  setSelectedFields: (aaids) => ({ type: SET_SELECTED_FIELDS, payload: aaids }),
  setWorking: (working) => ({ type: WORKING, payload: working }),
  splitField,
  toggleFieldFocus: (aaid) => ({ type: TOGGLE_FIELD_FOCUS, payload: aaid }),
  toggleFieldSelection: (payload) => ({ type: TOGGLE_FIELD_SELECTION, payload }),
  undo: () => ({ type: UNDO }),
  validateFields,
};

function unionPolygons(fc) {
  const features = fc.features || fc;
  if (!features.length) return null;
  if (features.length === 1) return features[0];

  return union(
    featureCollection(features.map((f) => (f.geometry ? f : feature(f)))),
  );
}

function pushBoundaryChange(features) {
  const shape = unionPolygons(features);

  if (shape) {
    const acres = Math.round((area(shape) / 4046.86) * 1000) / 1000;
    shape.properties = { ...shape.properties, acres };
  }

  return { type: ADD_BOUNDARY_CHANGE, payload: shape || null };
}

function loadFields(growerId) {
  return asyncAction(LOAD_FIELDS, async () => {
    const [certified, uncertified] = await Promise.all([
      api.getCertifiedFieldsForGrower(growerId),
      api.getUncertifiedFieldsForGrower(growerId),
    ]);

    const all = certified.concat(uncertified);
    return all.map((f) => ({ id: f.aaManagedId, ...f }));
  });
}

function withMapRefresh(fn) {
  return async (dispatch) => {
    const results = await fn();
    dispatch(mapActions.refresh());
    return results;
  };
}

function saveField(field) {
  return asyncAction(
    SAVE_FIELD,
    withMapRefresh(() => api.saveUncertifiedField(field)),
  );
}

function validateFields(aaids) {
  return asyncAction(
    VALIDATE_FIELDS,
    withMapRefresh(() => api.validateUncertifiedFields(aaids)),
  );
}

function fieldsCertified(payload) {
  return (dispatch) => {
    dispatch({ type: FIELDS_CERTIFIED, payload });
    dispatch(mapActions.refresh());
  };
}

function fieldsUncertified(payload) {
  return (dispatch) => {
    dispatch({ type: FIELDS_UNCERTIFIED, payload });
    dispatch(mapActions.refresh());
  };
}

function splitField(field, polygon1, polygon2) {
  const feature1 = unionPolygons(polygon1);
  const feature2 = unionPolygons(polygon2);

  return asyncAction(
    SPLIT_FIELD,
    withMapRefresh(() =>
      api
        .splitUncertifiedField(
          field,
          feature1.geometry || feature1,
          feature2.geometry || feature2,
        )
        .then((results) => ({ ...results, original: field })),
    ),
  );
}

function saveAttributes(growerId, fieldsInfo, attributes) {
  return asyncAction(SAVE_ATTRIBUTES, async (dispatch) =>
    api
      .bulkUpdateFields({ fieldsInfo, attributes })
      .then(() => dispatch(loadFields(growerId))),
  );
}

function reassignFields(growerId, newGrowerId, fieldsToReassign) {
  return asyncAction(REASSIGN_FIELDS, async (dispatch) =>
    api
      .reassignFields(growerId, { newGrowerId, fieldsToReassign })
      .then(() => dispatch(loadFields(growerId))),
  );
}

function explodeField(field, boundary) {
  return asyncAction(
    EXPLODE_FIELD,
    withMapRefresh(() =>
      api
        .explodeUncertifiedField(
          field,
          boundary.geometry.coordinates.map((coordinates) => ({
            type: 'Polygon',
            coordinates,
          })),
        )
        .then((results) => ({ ...results, original: field })),
    ),
  );
}

function deleteFields(fields) {
  return asyncAction(
    DELETE_FIELDS,
    withMapRefresh(() => api.deleteUncertifiedFields(fields)),
    { startPayload: fields, failurePayload: fields },
  );
}

function combineFields(fields) {
  return asyncAction(
    COMBINE_FIELDS,
    withMapRefresh(() =>
      api
        .combineUncertifiedFields(fields)
        .then((results) => ({ ...results, original: fields })),
    ),
  );
}

const reducerHandlers = {
  [ADD_BOUNDARY_CHANGE]: reduceAddBoundaryChange,
  [CLEAR_FAILURE]: reduceGenericSet.bind(null, 'error'),
  [CLEAR_RESULTS]: reduceClearResults,
  [COMBINE_FIELDS.complete]: reduceCombineFieldsComplete,
  [COMBINE_FIELDS.failure]: reduceWorkFailure,
  [COMBINE_FIELDS.start]: reduceWorkBegin,
  [DELETE_FIELDS.failure]: reduceDeleteFieldsFailure,
  [DELETE_FIELDS.start]: reduceDeleteFieldsStart,
  [EXPLODE_FIELD.complete]: reduceExplodeFieldComplete,
  [EXPLODE_FIELD.failure]: reduceWorkFailure,
  [EXPLODE_FIELD.start]: reduceWorkBegin,
  [FIELDS_CERTIFIED]: reduceFieldsCertified,
  [FIELDS_UNCERTIFIED]: reduceFieldsUncertified,
  [LOAD_FIELDS.complete]: reduceGenericSet.bind(null, 'fields'),
  [LOAD_FIELDS.failure]: reduceLoadFailure,
  [LOAD_FIELDS.start]: reduceLoadBegin,
  [REASSIGN_FIELDS.complete]: reduceReassignFieldsComplete,
  [REASSIGN_FIELDS.failure]: reduceWorkFailure,
  [REASSIGN_FIELDS.start]: reduceWorkBegin,
  [REDO]: reduceRedo,
  [RESET_BOUNDARY_CHANGES]: reduceResetBoundaryChanges,
  [SAVE_ATTRIBUTES.complete]: reduceSaveAttributesComplete,
  [SAVE_ATTRIBUTES.failure]: reduceWorkFailure,
  [SAVE_ATTRIBUTES.start]: reduceWorkBegin,
  [SAVE_FIELD.complete]: reduceSaveFieldComplete,
  [SAVE_FIELD.failure]: reduceWorkFailure,
  [SAVE_FIELD.start]: reduceWorkBegin,
  [SET_MAP]: reduceGenericSet.bind(null, 'map'),
  [SET_SELECTED_FIELDS]: reduceSetSelected,
  [SPLIT_FIELD.complete]: reduceSplitFieldComplete,
  [SPLIT_FIELD.failure]: reduceWorkFailure,
  [SPLIT_FIELD.start]: reduceWorkBegin,
  [TOGGLE_FIELD_FOCUS]: reduceToggleFieldFocus,
  [TOGGLE_FIELD_SELECTION]: reduceToggleFieldSelection,
  [UNDO]: reduceUndo,
  [VALIDATE_FIELDS.complete]: reduceValidateFieldsComplete,
  [VALIDATE_FIELDS.failure]: reduceValidateFieldsFailure,
  [VALIDATE_FIELDS.start]: reduceValidateFieldsStart,
  [WORKING]: reduceGenericSet.bind(null, 'working'),
};

function reduceSetSelected(state, selected) {
  state.selected = selected;
  state.focused = null;
}
function reduceUndo(state) {
  if (state.boundaryChanges.length > state.boundaryChangesIndex + 1) {
    state.boundaryChangesIndex += 1;
  }
}

function reduceRedo(state) {
  if (state.boundaryChangesIndex > 0) {
    state.boundaryChangesIndex -= 1;
  }
}

function reduceResetBoundaryChanges(state) {
  state.boundaryChanges = [];
  state.boundaryChangesIndex = 0;
}

function reduceAddBoundaryChange(state, change) {
  state.boundaryChanges.splice(0, state.boundaryChangesIndex, change);
  state.boundaryChangesIndex = 0;
}

function reduceDeleteFieldsStart(state, fields) {
  fields.map(removeField.bind(null, state));
}

function reduceClearResults(state) {
  delete state.validationResults;
  delete state.certificationResults;
  delete state.uncertificationResults;
}

function reduceDeleteFieldsFailure(state, error, fields) {
  state.error = error?.message || error;
  fields.forEach(state.fields.push);
}

function reduceGenericSet(key, state, value) {
  state[key] = value;
}

function reduceLoadBegin(state) {
  delete state.fields;
  delete state.error;
}

function reduceLoadFailure(state, error) {
  state.error = error?.message || error;
}

function reduceWorkBegin(state) {
  state.error = null;
  state.working = true;
}

function reduceSaveFieldComplete(state, newField) {
  updateField(state, newField);
  state.working = false;
  state.focused = null;
  state.selected = [newField.aaManagedId];
  state.boundaryChanges = [];
  state.boundaryChangesIndex = 0;
}

function reduceSaveAttributesComplete(state) {
  state.working = false;
}

function reduceReassignFieldsComplete(state) {
  state.working = false;
}

function reduceValidateFieldsStart(state) {
  state.validationResults = null;
}

function reduceValidateFieldsFailure(state, error) {
  state.validationResults = [];
  state.error = error?.message || error;
}

function mapValidationField(field) {
  return {
    aaManagedId: field.aaManagedId,
    createdDate: field.createdDate,
    validated: field.validated,
    validateError: field.validated === 'passed' ? '' : field.validateError,
  };
}

function reduceValidateFieldsComplete(state, results) {
  state.validationResults = results;
  results.forEach(({ uncertifiedField: field }) =>
    updateField(state, mapValidationField(field)),
  );
}

function reduceFieldsCertified(state, payload) {
  const { results = [], error } = payload;
  state.error = error?.message || error;
  state.certificationResults = results.map((result) => result.validationResult);
  results.forEach((result) => {
    updateField(state, {
      ...mapValidationField(result.validationResult.uncertifiedField),
      isCertified: result.success,
    });
  });
}

function reduceFieldsUncertified(state, payload) {
  const { results = [], error } = payload;
  state.error = error?.message || error;
  state.uncertificationResults = results.map((field) => ({
    validationResult: 'passed',
    uncertifiedField: field,
    errors: [],
  }));

  results.forEach((field) =>
    updateField(state, { aaManagedId: field.aaManagedId, isCertified: false }),
  );
}

function reduceWorkFailure(state, error) {
  logger.error(error);
  state.error = error?.message || error;
  state.working = false;
}

function updateField(state, newField) {
  const existing = state.fields.find(
    (f) => f.aaManagedId === newField.aaManagedId,
  );

  if (!existing) {
    state.fields.push({ ...newField, id: newField.aaManagedId });
  } else {
    Object.assign(existing, newField);
  }
}

function removeField(state, field) {
  state.fields = state.fields.filter(
    (f) => f.aaManagedId !== field.aaManagedId,
  );
}

function reduceSplitFieldComplete(state, { original, field1, field2 }) {
  removeField(state, original);
  updateField(state, field1);
  updateField(state, field2);
  state.working = false;
  state.focused = null;
  state.selected = [field1.aaManagedId, field2.aaManagedId];
}

function reduceCombineFieldsComplete(state, { original, ...winner }) {
  original.forEach(removeField.bind(null, state));
  updateField(state, winner);
  state.working = false;
  state.focused = null;
  state.selected = [winner.aaManagedId];
}

function reduceExplodeFieldComplete(state, { original, fields }) {
  removeField(state, original);
  fields.map(updateField.bind(null, state));
  state.working = false;
  state.focused = null;
  state.selected = fields.map((f) => f.aaManagedId);
}

function reduceToggleFieldSelection(state, aaid) {
  state.selected = state.selected || [];

  if (state.selected.includes(aaid)) {
    state.selected = state.selected.filter((f) => f !== aaid);
  } else if (aaid) {
    state.selected.push(aaid);
  }
}

function reduceToggleFieldFocus(state, aaid) {
  state.focused = state.focused === aaid ? null : aaid;
}

export const reducers = {
  [SLICE]: (state = {}, { type, payload } = {}) => {
    if (!reducerHandlers[type]) return state;
    return produce(state, (draft) => reducerHandlers[type](draft, payload));
  },
};

function buildBoundingBox(fields) {
  if (fields?.length) {
    const features = fields.map((f) => f.shape && feature(f.shape));
    const boundaries = featureCollection(features.filter(Boolean));
    return bbox(boundaries);
  }
}
